import { CSSProperties, memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CheckBox } from '@components/Forms';
import { FormType, Translation } from '@enums';
import { useAppDispatch, useFormList, useFormSelectedList } from '@hooks';
import { toggleCheckBox } from '@store';
import type { Column, ColumnLayout, EndpointType, FormMetadata } from '@types';
import { cn, extractFormIds, validateSameArray, validateValueEqual } from '@utils';

// Types
interface DraggableHeaderCellProps extends FormMetadata {
  column: Column;
  layout: ColumnLayout;
  colId: number | string;
}

interface HeaderCellStyleProps {
  layout: ColumnLayout;
  column: Column;
  colId: number | string;
}

interface CheckboxHeaderProps {
  colId: number | string;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  onToggle: () => void;
}

// Constants
const FIRST_COLUMN_INDEX = 0;
const FIRST_COLUMN_PADDING = 'px-6';
const DEFAULT_COLUMN_PADDING = 'px-2.5';

// Style generator functions
const generateHeaderCellStyles = ({ layout }: HeaderCellStyleProps): CSSProperties => ({
  width: `${layout.percentage}%`,
  minWidth: layout.minWidth,
  flex: layout.flex,
});

const generateHeaderCellClassNames = ({ column, colId }: HeaderCellStyleProps): string => {
  const baseClasses = 'py-4 text-xs font-normal text-gray-400';
  const alignmentClass = column.center ? 'text-center' : 'text-left';
  const paddingClass = colId === FIRST_COLUMN_INDEX ? FIRST_COLUMN_PADDING : DEFAULT_COLUMN_PADDING;

  return cn(baseClasses, alignmentClass, paddingClass);
};

// Custom hooks
const useCheckboxColumnLogic = (
  column: Column,
  formType: FormType,
  endpoint?: EndpointType,
): {
  isCheckboxColumn: boolean;
  hasFormItems: boolean;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  handleToggleAll: () => void;
} => {
  const dispatch = useAppDispatch();
  const { t: tTables } = useTranslation(Translation.Configuration, { keyPrefix: 'tables' });

  const formList = useFormList({ formType, endpoint });
  const selectedFormIds = useFormSelectedList({ formType, endpoint });

  const checkboxLabel = tTables('checkBox');
  const isCheckboxColumn = validateValueEqual(column.label, checkboxLabel);
  const hasFormItems = formList.length > 0;

  const formItemIds = useMemo(() => {
    if (!isCheckboxColumn || !hasFormItems) return [];
    return extractFormIds(formList);
  }, [isCheckboxColumn, hasFormItems, formList]);

  const selectionState = useMemo(() => {
    if (!isCheckboxColumn || !hasFormItems) {
      return { isAllSelected: false, isIndeterminate: false };
    }

    const hasSelectedItems = selectedFormIds.length > 0;
    const isAllSelected = validateSameArray(selectedFormIds, formItemIds) && hasSelectedItems;
    const isIndeterminate = hasSelectedItems && !isAllSelected;

    return { isAllSelected, isIndeterminate };
  }, [isCheckboxColumn, hasFormItems, selectedFormIds, formItemIds]);

  const handleToggleAll = useCallback(() => {
    if (!isCheckboxColumn || !hasFormItems) return;
    dispatch(toggleCheckBox({ formType, endpoint }));
  }, [dispatch, formType, endpoint, isCheckboxColumn, hasFormItems]);

  return {
    isCheckboxColumn,
    hasFormItems,
    isAllSelected: selectionState.isAllSelected,
    isIndeterminate: selectionState.isIndeterminate,
    handleToggleAll,
  };
};

// Sub-components
const CheckboxHeader = memo<CheckboxHeaderProps>(({ colId, isAllSelected, isIndeterminate, onToggle }) => (
  <CheckBox
    id={colId}
    checked={isAllSelected}
    indeterminate={isIndeterminate}
    onChange={onToggle}
    aria-label={isAllSelected ? 'Deselect all items' : 'Select all items'}
  />
));

CheckboxHeader.displayName = 'CheckboxHeader';

const HeaderContent = memo<{
  isCheckboxColumn: boolean;
  hasFormItems: boolean;
  label: string;
}>(({ isCheckboxColumn, hasFormItems, label }) => {
  if (isCheckboxColumn && !hasFormItems) {
    return null;
  }

  if (!isCheckboxColumn) {
    return <span>{label}</span>;
  }

  return null;
});

HeaderContent.displayName = 'HeaderContent';

export const HeaderCell = memo<DraggableHeaderCellProps>(({ column, colId, formType, endpoint, layout }) => {
  const { isCheckboxColumn, hasFormItems, isAllSelected, isIndeterminate, handleToggleAll } = useCheckboxColumnLogic(
    column,
    formType,
    endpoint,
  );

  const cellStyles = useMemo(() => generateHeaderCellStyles({ layout, column, colId }), [layout, column, colId]);

  const cellClassNames = useMemo(
    () => generateHeaderCellClassNames({ layout, column, colId }),
    [layout, column, colId],
  );

  const renderContent = () => {
    if (isCheckboxColumn && hasFormItems) {
      return (
        <CheckboxHeader
          colId={colId}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
          onToggle={handleToggleAll}
        />
      );
    }

    return <HeaderContent isCheckboxColumn={isCheckboxColumn} hasFormItems={hasFormItems} label={column.label} />;
  };

  return (
    <div className={cellClassNames} style={cellStyles} role="columnheader" aria-sort="none">
      {renderContent()}
    </div>
  );
});

HeaderCell.displayName = 'DraggableHeaderCell';
